import { Button, Flex } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { TextareaField } from 'shared/ui/Fields';
import { Form, FormButton } from 'shared/ui/Form';
import { useUpdateNote } from '../hooks';

type EditNoteModalBodyProps = {
  documentId: number;
  notes: string;
  onClose: () => void;
};

const EditNoteModalBody: React.FC<EditNoteModalBodyProps> = ({
  documentId,
  notes,
  onClose,
}) => {
  const { t } = useTranslation(['common']);
  const { updateNote } = useUpdateNote(documentId);

  const handleSubmit = async ({ notes }: { notes: string }) => {
    await updateNote(notes);
    onClose();
  };

  return (
    <Form defaultValues={{ notes }} onSubmit={handleSubmit}>
      <TextareaField
        name="notes"
        autosize
        minRows={8}
        data-testid="field-edit-note"
      />
      <Flex
        gap={8}
        justify="flex-end"
        mt={16}
        pt={8}
        pos="sticky"
        bottom={0}
        bg="white"
      >
        <Button
          onClick={onClose}
          variant="default"
          data-testid="btn-cancel-edit-note"
        >
          {t('cancel', { ns: 'common' })}
        </Button>
        <FormButton type="submit" data-testid="btn-save-note">
          {t('save', { ns: 'common' })}
        </FormButton>
      </Flex>
    </Form>
  );
};

const MODAL_ID = 'edit-note-modal';

const Title = () => {
  const { t } = useTranslation(['documents']);
  return <>{t('data.notes.edit', { ns: 'documents' })}</>;
};

export type EditNoteModalProps = {
  documentId: number;
  notes?: string | null;
};

export const editNoteModal = ({ documentId, notes }: EditNoteModalProps) => {
  openModal({
    modalId: MODAL_ID,
    title: <Title />,
    centered: true,
    children: (
      <EditNoteModalBody
        documentId={documentId}
        notes={notes ?? ''}
        onClose={() => closeModal(MODAL_ID)}
      />
    ),
  });
};
